import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import TranslateIcon from '@mui/icons-material/Translate';
import { Box } from '@mui/material';
import {
  DataGridPro,
  GridActionsCellItem,
  GridColDef,
  GridRowId,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getCurrentLanguage, getLanguageLabel } from '~/localization';

type RowData = {
  id: string;
  description: string;
  name: string;
};
type Props = {
  customColumns?: GridColDef[];
  rows: RowData[];
  editTerm?: (id: GridRowId) => void;
  translateTerm?: (id: GridRowId) => void;
  deleteTerm?: (id: GridRowId) => void;
};

export const LegacyAdminList = ({
  customColumns = [],
  rows,
  editTerm,
  deleteTerm,
  translateTerm,
}: Props) => {
  const { t } = useTranslation();
  const currentLanguage = getCurrentLanguage();
  const currentLanguageLabel = getLanguageLabel(currentLanguage);
  const [pageSize, setPageSize] = useState<number>(10);

  const defaultColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'description',
        headerName: t('laTerm'),
        width: 150,
      },
      {
        field: 'name',
        headerName: currentLanguageLabel,
        width: 150,
      },
    ],
    [currentLanguageLabel, t],
  );

  const actionColumn: GridColDef[] = useMemo(
    () => [
      {
        field: 'actions',
        type: 'actions',
        width: 80,
        // eslint-disable-next-line no-sparse-arrays
        getActions: (params: GridRowParams) => [
          ...(editTerm
            ? [
                <GridActionsCellItem
                  icon={<EditIcon />}
                  label={t('laEdit')}
                  onClick={() => editTerm(params.id)}
                  showInMenu
                />,
              ]
            : []),
          ...(translateTerm
            ? [
                <GridActionsCellItem
                  icon={<TranslateIcon />}
                  label={t('title_translate')}
                  onClick={() => translateTerm(params.id)}
                  showInMenu
                />,
              ]
            : []),
          ,
          ...(deleteTerm
            ? [
                <GridActionsCellItem
                  icon={<DeleteIcon />}
                  label={t('laDelete')}
                  onClick={() => deleteTerm(params.id)}
                  showInMenu
                />,
              ]
            : []),
          ,
        ],
      },
    ],
    [deleteTerm, editTerm, t, translateTerm],
  );

  return (
    <Box sx={{ width: '100%' }}>
      <DataGridPro
        autoHeight
        pagination
        disableSelectionOnClick
        columns={[...defaultColumns, ...customColumns, ...actionColumn]}
        rows={rows}
        pageSize={pageSize}
        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
        rowHeight={38}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Box>
  );
};
